import api from './api';
import { cache } from '../utils/cache';
import type { Roaster, RoasterFormData } from '../types/roaster';

interface RoasterDTO {
  id: string;
  name: string;
  status?: string;
  is_active?: boolean;
  isActive?: boolean;
  notes?: string;
  internal_notes?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

type RoasterListResponse = RoasterDTO[] | { data: RoasterDTO[] };

const mapDtoToRoaster = (data: RoasterDTO): Roaster => ({
  id: data.id,
  name: data.name,
  isActive: data.status ? data.status === 'active' : (data.is_active ?? data.isActive ?? true),
  notes: data.notes ?? data.internal_notes,
  createdAt: data.created_at ?? data.createdAt,
  updatedAt: data.updated_at ?? data.updatedAt,
});

const getListPayload = (payload: RoasterListResponse): RoasterDTO[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

export const roasterService = {
  getAll: async (opts?: { includeOnboardingSamples?: boolean }): Promise<Roaster[]> => {
    const includeSamples = opts?.includeOnboardingSamples === true;
    const fetcher = async () => {
      const params = includeSamples ? { include_onboarding_samples: 'true' } : undefined;
      const response = await api.get<RoasterListResponse>('/roasters', { params });
      const items = getListPayload(response.data);
      return items.map(mapDtoToRoaster);
    };
    // Onboarding sample data is volatile (seeded/cleaned mid-tour); never serve the
    // sample-inclusive list from the 5-min cache, or a fetch that ran before the
    // seed would strand the pickers with a stale sample-less list.
    if (includeSamples) return fetcher();
    return cache.fetch('roasters_all', fetcher);
  },

  getById: async (id: string): Promise<Roaster> => {
    return cache.fetch(`roasters_${id}`, async () => {
      const response = await api.get<RoasterDTO>(`/roasters/${id}`);
      return mapDtoToRoaster(response.data);
    });
  },

  create: async (data: RoasterFormData): Promise<Roaster> => {
    const payload = {
      name: data.name,
      status: data.isActive ? 'active' : 'inactive',
      notes: data.notes,
    };
    const response = await api.post<RoasterDTO>('/roasters', payload);
    cache.invalidate('roasters_all');
    return mapDtoToRoaster(response.data);
  },

  update: async (id: string, data: RoasterFormData): Promise<Roaster> => {
    const payload = {
      name: data.name,
      status: data.isActive ? 'active' : 'inactive',
      notes: data.notes,
    };
    const response = await api.put<RoasterDTO>(`/roasters/${id}`, payload);
    cache.delete(`roasters_${id}`);
    cache.invalidate('roasters_all');
    return mapDtoToRoaster(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/roasters/${id}`);
    cache.delete(`roasters_${id}`);
    cache.invalidate('roasters_all');
  },
};
